import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatHours, TOOLTIP_CURSOR, TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

export interface CohortSegment {
  key: string;
  label: string;
  color: string;
}

export interface CohortRow {
  /** Cohort name, e.g. "FTE" or "< 6 mo". */
  cohort: string;
  values: Record<string, number>;
  /** Right-hand annotation, e.g. "12 ppl". */
  annotation: string;
  /** Summary line printed under the chart. */
  footnote: string;
}

/**
 * Cohort comparison for a drilldown: a couple of horizontal stacked bars plus
 * a plain-language summary of each.
 *
 * **Horizontal on purpose.** The dashboard's own charts are vertical, so this
 * shape signals "you are looking at a drilldown, not another tile" — the same
 * reason the panel carries its own border and badge.
 */
export function CohortBarChart({
  title,
  rows,
  segments,
  height = 132,
}: {
  title: string;
  rows: CohortRow[];
  segments: CohortSegment[];
  height?: number;
}) {
  const animation = useChartAnimation();
  const data = rows.map((r) => ({ cohort: r.cohort, annotation: r.annotation, ...r.values }));
  const labelFor = new Map(segments.map((s) => [s.key, s.label]));

  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1 font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
        {title}
      </p>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 4, right: 46, bottom: 0, left: 4 }}
            barCategoryGap={12}
          >
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="cohort"
              width={76}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--foreground)' }}
            />
            <Tooltip
              cursor={TOOLTIP_CURSOR}
              contentStyle={TOOLTIP_STYLE}
              formatter={(value, key) => [
                `${formatHours(toNumber(value))} hrs`,
                labelFor.get(toKey(key)) ?? toKey(key),
              ]}
            />
            {segments.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="a"
                fill={s.color}
                radius={i === segments.length - 1 ? [0, 4, 4, 0] : 0}
                {...animation}
              >
                {i === segments.length - 1 ? (
                  <LabelList
                    dataKey="annotation"
                    position="right"
                    style={{ fontSize: 10, fontWeight: 700, fill: 'var(--muted-foreground)' }}
                  />
                ) : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="space-y-0.5 text-[11px] text-muted-foreground">
        {rows.map((r) => (
          <li key={r.cohort}>{r.footnote}</li>
        ))}
      </ul>
    </div>
  );
}
